/**
 * Dynamic-import ("chunk load") failure detection plus the one-shot reload
 * guard used by the lazy-route error boundary.
 *
 * Two very different failures surface as the same rejected `import()`:
 *
 *  - **Stale deploy.** The browser is holding an `index.html` that references
 *    chunk hashes CloudFront no longer serves. Re-running the same `import()`
 *    can never succeed — only fresh HTML fixes it, so we hard-reload once.
 *  - **Flaky / absent network.** The chunk URL is valid, the request just
 *    failed. Reloading here would throw away the running app and can land the
 *    user on the browser's offline page, so we keep the app mounted and let
 *    them re-attempt the import instead.
 *
 * The reload is deliberately spent at most once per tab session: a deploy that
 * is still mid-flight (or an `index.html` that keeps pointing at a missing
 * chunk) would otherwise reload forever.
 */

export const CHUNK_RELOAD_MARKER_KEY = 'ascend-chunk-reload-attempted'

/**
 * Message fragments engines use when a dynamic import fails. Vite's preload
 * helper rethrows the raw browser error, so this covers all four engines, the
 * CSS-preload variant, and the MIME-type message a SPA HTML fallback produces
 * when it answers a request for a deleted chunk with `index.html`.
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css',
  'failed to load module script',
  'expected a javascript module script',
  'loading chunk',
  'loading css chunk',
]

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message?: unknown }
    if (typeof message === 'string') return message
  }
  return ''
}

/** True when `error` looks like a failed route-chunk fetch rather than a render bug. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  if (error && typeof error === 'object' && 'name' in error) {
    if ((error as { name?: unknown }).name === 'ChunkLoadError') return true
  }
  const message = messageOf(error).toLowerCase()
  if (!message) return false
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

/** Whether this tab session has already spent its automatic hard reload. */
export function hasAttemptedChunkReload(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_MARKER_KEY) === '1'
  } catch {
    // Storage denied (Safari private mode, blocked cookies): treat the reload as
    // already spent, because without a durable marker we could not stop a loop.
    return true
  }
}

/**
 * Claim the one automatic reload for this tab session.
 *
 * Returns whether the marker is genuinely readable afterwards — the caller must
 * only reload when this is `true`, since a marker that does not survive the
 * reload is exactly the reload loop this guard exists to prevent.
 */
export function markChunkReloadAttempted(): boolean {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_MARKER_KEY, '1')
    return sessionStorage.getItem(CHUNK_RELOAD_MARKER_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Re-arm the automatic reload after a route chunk has loaded successfully.
 *
 * Called from a committed effect inside the lazy wrapper, which is proof that
 * chunk fetching works *right now*. That keeps the guard scoped to consecutive
 * failures: a second deploy later in the same long-lived session can still
 * self-heal, but a chunk that never loads only ever triggers one reload.
 */
export function clearChunkReloadMarker(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_MARKER_KEY)
  } catch {
    /* best-effort */
  }
}

/** `true` unless the browser positively reports itself as offline. */
export function isBrowserOnline(): boolean {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine !== false
}

export type LazyRouteRecovery = 'reload' | 'manual'

/**
 * Decide how to recover from an error thrown inside the lazy-route subtree.
 *
 * `reload` (fetch fresh HTML, self-healing) is reserved for the stale-deploy
 * shape: a genuine chunk-fetch failure, while online, with the session's one
 * automatic reload still unspent. Everything else — render bugs, offline
 * devices, and any repeat failure — falls back to `manual`, i.e. show the
 * error state and let the user choose.
 */
export function decideLazyRouteRecovery(input: {
  error: unknown
  online: boolean
  reloadAlreadyAttempted: boolean
}): LazyRouteRecovery {
  const { error, online, reloadAlreadyAttempted } = input
  if (!isChunkLoadError(error)) return 'manual'
  if (!online) return 'manual'
  if (reloadAlreadyAttempted) return 'manual'
  return 'reload'
}
